import React, { useState, useEffect } from 'react';
import { Alert, Button } from 'antd';
import { CloseOutlined, InfoCircleFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { getResponsiveStyles } from '../../ux/SystemAlerts/styles/systemAlertBar.styles';
import '../../ux/SystemAlerts/styles/systemAlertBar.css';

// Helper function to ensure URL has protocol
const ensureProtocol = (url) => {
  if (!url) return '';
  // If URL already has protocol, return as is
  if (/^https?:\/\//.test(url)) {
    return url;
  }
  // If URL starts with //, add https: before it
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  // Otherwise, add https:// prefix
  return `https://${url}`;
};

const AlertWithAnimation = ({ alert, onDismiss, index, styles, hiddenCount }) => {
  const [isDismissing, setIsDismissing] = useState(false);
  const [isHoveringClose, setIsHoveringClose] = useState(false);

  useEffect(() => {
    let timeoutId;
    if (isDismissing) {
      timeoutId = setTimeout(() => {
        onDismiss(alert.id);
      }, 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isDismissing, alert.id, onDismiss]);

  const handleDismiss = () => {
    setIsDismissing(true);
  };

  const getAlertAction = () => {
    const actions = [];

    if (alert.link && alert.linkText) {
      actions.push(
        <Button
          key="link"
          size="small"
          type="link"
          onClick={() => window.open(ensureProtocol(alert.link), '_blank')}
          style={styles.linkButton}
        >
          {alert.linkText}
        </Button>
      );
    }

    if (alert.dismissible) {
      actions.push(
        <span
          key="dismiss"
          onClick={handleDismiss}
          onMouseEnter={() => setIsHoveringClose(true)}
          onMouseLeave={() => setIsHoveringClose(false)}
          onKeyDown={(e) => e.key === 'Enter' && handleDismiss()}
          role="button"
          tabIndex={0}
          aria-label="Dismiss alert"
          style={{
            ...styles.closeButton,
            ...(isHoveringClose ? styles.closeButtonHover : {}),
          }}
        >
          <CloseOutlined style={{ fontSize: '12px' }} />
        </span>
      );
    }

    return actions.length > 0 ? actions : null;
  };

  const getAlertType = (type) => {
    const typeMap = {
      INFO: 'info',
      WARNING: 'warning',
      ERROR: 'error',
      SUCCESS: 'success',
    };
    return typeMap[type] || 'info';
  };

  return (
    <div
      style={{
        ...styles.alertWrapper,
        ...(isDismissing ? styles.alertWrapperDismissing : {}),
        animationDelay: `${index * 0.1}s`,
      }}
    >
      <Alert
        message={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={styles.alertMessage}>{alert.message}</span>
            {hiddenCount > 0 && (
              <span style={{
                marginLeft: '16px',
                fontSize: '13px',
                color: 'rgba(0, 0, 0, 0.65)',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}>
                +{hiddenCount} more
              </span>
            )}
          </div>
        }
        type={getAlertType(alert.type)}
        banner
        showIcon
        closable={false}
        style={styles.alert}
        action={getAlertAction()}
      />
    </div>
  );
};

AlertWithAnimation.propTypes = {
  alert: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['INFO', 'WARNING', 'ERROR', 'SUCCESS']).isRequired,
    message: PropTypes.string.isRequired,
    link: PropTypes.string,
    linkText: PropTypes.string,
    dismissible: PropTypes.bool.isRequired,
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  styles: PropTypes.object.isRequired,
  hiddenCount: PropTypes.number,
};

const SystemAlertBar = ({ alerts, hiddenCount, onDismiss }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!alerts || alerts.length === 0) {
    return null;
  }

  const styles = getResponsiveStyles(isMobile);

  return (
    <div style={styles.alertContainer}>
      {alerts.map((alert, index) => (
        <AlertWithAnimation
          key={alert.id}
          alert={alert}
          onDismiss={onDismiss}
          index={index}
          styles={styles}
          hiddenCount={index === 0 ? hiddenCount : 0}
        />
      ))}
    </div>
  );
};

SystemAlertBar.propTypes = {
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['INFO', 'WARNING', 'ERROR', 'SUCCESS']).isRequired,
      message: PropTypes.string.isRequired,
      link: PropTypes.string,
      linkText: PropTypes.string,
      dismissible: PropTypes.bool.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      priority: PropTypes.number.isRequired,
    })
  ),
  hiddenCount: PropTypes.number,
  onDismiss: PropTypes.func.isRequired,
};

export default SystemAlertBar;
